/**
 * ImageCache - IndexedDB-based image caching with FIFO eviction.
 *
 * Stores fully-fetched image blobs keyed by their normalized URL (origin + pathname),
 * which makes the cache stable across AWS pre-signed URLs whose query string changes
 * on every signing.
 *
 * The cache is gated on demo mode: outside demo mode, all reads/writes are no-ops.
 * Demo mode entries never expire — eviction is FIFO when the 50MB cap is exceeded.
 */

import environment from 'utils/environment';

const DB_NAME = 'CareInnImageCache';
const STORE_NAME = 'images';
const MAX_CACHE_SIZE = 50 * 1024 * 1024;

class ImageCache {
  constructor() {
    this.db = null;
    this.initPromise = this.initDB();
  }

  /**
   * Strip query string and fragment so AWS pre-signed URLs collapse to a stable key.
   * Example:
   *   https://example.com/image.jpg?X-Amz-Signature=abc&X-Amz-Date=xyz
   *   → https://example.com/image.jpg
   */
  normalizeUrl(url) {
    if (!url) return url;
    try {
      const urlObj = new URL(url);
      return urlObj.origin + urlObj.pathname;
    } catch (err) {
      console.warn(`[ImageCache] Failed to normalize URL: ${url}`, err);
      return url;
    }
  }

  async initDB() {
    return new Promise((resolve, reject) => {
      if (this.db) return resolve(this.db);

      const request = indexedDB.open(DB_NAME, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'url' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async getCacheSize() {
    await this.initPromise;
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const totalSize = request.result.reduce((sum, item) => sum + item.size, 0);
        resolve(totalSize);
      };
    });
  }

  async getAllEntries() {
    await this.initPromise;
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('timestamp');
      const request = index.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        resolve(request.result);
      };
    });
  }

  async removeOldest() {
    const entries = await this.getAllEntries();
    if (entries.length === 0) return;
    await this.remove(entries[0].url);
  }

  /**
   * Cache a blob under the normalized URL. FIFO-evicts oldest entries until the
   * new blob fits within MAX_CACHE_SIZE. No-op when demo mode is off.
   */
  async put(url, blob) {
    if (!environment.isDemoMode()) return null;
    await this.initPromise;

    const normalizedUrl = this.normalizeUrl(url);
    const size = blob.size;
    const currentSize = await this.getCacheSize();

    let newSize = currentSize + size;
    while (newSize > MAX_CACHE_SIZE) {
      await this.removeOldest();
      newSize = (await this.getCacheSize()) + size;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const entry = {
        url: normalizedUrl,
        blob,
        size,
        timestamp: Date.now(),
      };

      const request = store.put(entry);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(entry);
    });
  }

  /**
   * Look up a cached blob by raw URL. Returns null when demo mode is off
   * or there is no entry.
   */
  async get(url) {
    if (!environment.isDemoMode()) return null;
    await this.initPromise;

    const normalizedUrl = this.normalizeUrl(url);

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(normalizedUrl);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.blob : null);
      };
    });
  }

  /**
   * Returns false when demo mode is off or no entry exists.
   */
  async has(url) {
    if (!environment.isDemoMode()) return false;
    await this.initPromise;

    const normalizedUrl = this.normalizeUrl(url);

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(normalizedUrl);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        resolve(!!request.result);
      };
    });
  }

  async remove(url) {
    await this.initPromise;

    const normalizedUrl = this.normalizeUrl(url);

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(normalizedUrl);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async clear() {
    await this.initPromise;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
}

const imageCacheInstance = new ImageCache();
export default imageCacheInstance;
